/**
 * TextPopoverEditor — floating popover with an auto-growing <textarea> for
 * editing long text (task name / comments) without truncation.
 *
 * Behaviour:
 *   • Renders as an absolutely-positioned panel above the trigger cell.
 *   • Save on Ctrl+Enter, on click of "Save", or on outside click (blur).
 *   • Cancel on Escape.
 *   • Auto-focuses and auto-selects the existing text on mount.
 */
import React, { useEffect, useRef, useState } from "react";
import { Save, X } from "lucide-react";

export default function TextPopoverEditor({
  value = "",
  onSave,
  onCancel,
  placeholder = "Type here…",
  minRows = 3,
  maxRows = 12,
  label = "",
  testId = "",
  width = 420,
}) {
  const [text, setText] = useState(value ?? "");
  const wrapRef = useRef(null);
  const textareaRef = useRef(null);

  // Focus on mount and place cursor at the end.
  useEffect(() => {
    const t = textareaRef.current;
    if (!t) return;
    t.focus();
    const len = t.value.length;
    t.setSelectionRange(len, len);
    autosize(t);
  }, []);

  // Outside click → save
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        onSave?.(text);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [text, onSave]);

  const autosize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    const min = lineHeight * minRows;
    const max = lineHeight * maxRows;
    const desired = Math.min(Math.max(el.scrollHeight, min), max);
    el.style.height = `${desired}px`;
  };

  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); onCancel?.(); return; }
    if (e.key === "Enter") {
      if (e.ctrlKey || e.metaKey) {
        // Ctrl/Cmd + Enter → insert a newline at the caret position (no save)
        e.preventDefault();
        const el = e.target;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const newText = text.slice(0, start) + "\n" + text.slice(end);
        setText(newText);
        // Restore caret + resize on next tick
        requestAnimationFrame(() => {
          el.selectionStart = el.selectionEnd = start + 1;
          autosize(el);
        });
      } else if (!e.shiftKey) {
        // Plain Enter → save (Shift+Enter still inserts newline natively)
        e.preventDefault();
        onSave?.(text);
      }
    }
  };

  return (
    <div ref={wrapRef}
         className="absolute z-50 left-0 top-0 rounded-xl border border-cyan-400/40 bg-[#0f1a2e] shadow-2xl p-2"
         style={{ width, minWidth: 320 }}
         data-testid={testId ? `${testId}-popover` : "text-popover"}
         onClick={(e) => e.stopPropagation()}>
      {label && (
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-cyan-300 font-black mb-1.5 px-1">
          <span>{label}</span>
          <span className="text-slate-500 normal-case tracking-normal font-normal ml-auto">
            <kbd className="px-1 py-0.5 rounded bg-white/5 font-mono">Enter</kbd> save · <kbd className="px-1 py-0.5 rounded bg-white/5 font-mono">Ctrl</kbd>+<kbd className="px-1 py-0.5 rounded bg-white/5 font-mono">Enter</kbd> new line · <kbd className="px-1 py-0.5 rounded bg-white/5 font-mono">Esc</kbd> cancel
          </span>
        </div>
      )}
      <textarea ref={textareaRef}
                value={text}
                onChange={(e) => { setText(e.target.value); autosize(e.target); }}
                onKeyDown={onKey}
                placeholder={placeholder}
                className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-2 py-1.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-400 resize-none leading-snug"
                data-testid={testId ? `${testId}-textarea` : "text-popover-textarea"}/>
      <div className="flex items-center justify-end gap-1 mt-1.5">
        <button type="button" onClick={() => onCancel?.()}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] uppercase tracking-widest font-black text-slate-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid={testId ? `${testId}-cancel` : undefined}>
          <X className="w-3 h-3"/> Cancel
        </button>
        <button type="button" onClick={() => onSave?.(text)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] uppercase tracking-widest font-black bg-cyan-500 hover:bg-cyan-400 text-slate-900"
                data-testid={testId ? `${testId}-save` : undefined}>
          <Save className="w-3 h-3"/> Save
        </button>
      </div>
    </div>
  );
}
